import { useEffect, useState } from 'react';
import { Button, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { NumberInput, Select, TextInput } from '@repletoryreach/sdk-ui/inputs';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { isApiError, type admin } from '@repletoryreach/sdk-api';
import { useUpdateLanguage } from '../../../../data/useLanguages';
import { WordsInput } from '../../../../components/WordsInput';
import { useT } from '../../../../i18n/I18nProvider';
import { META_CODE, NAME_MAX, NATIVE_MAX, QUALITIES, SPEECH_CODE, WORDS_MAX, WORD_LENGTH_MAX, type Quality } from '../languages';

type Field = 'name' | 'native_name' | 'quality' | 'meta_codes' | 'speech_code' | 'change_words' | 'greeting_words' | 'yes_words' | 'no_words' | 'sort_order';
const FIELDS: readonly Field[] = ['name', 'native_name', 'quality', 'meta_codes', 'speech_code', 'change_words', 'greeting_words', 'yes_words', 'no_words', 'sort_order'];
type WordField = 'change_words' | 'greeting_words' | 'yes_words' | 'no_words';
const WORD_FIELDS: readonly WordField[] = ['change_words', 'greeting_words', 'yes_words', 'no_words'];

/** The service's refusal, as `{ field, message }`, when it names a field this drawer shows. */
function fieldRefusal(e: unknown): { field: Field | null; message: string } | null {
  if (!isApiError(e)) return null;
  const err = (e.body as { error?: { field?: string; message?: string } } | null)?.error;
  const field = FIELDS.find((f) => f === err?.field) ?? null;
  return { field, message: err?.message ?? e.message };
}

const same = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((v, i) => v === b[i]);

/**
 * One language's names, words and codes: its English name, its own name (which goes on the language menu's WhatsApp
 * buttons, so at most 20 characters), how well the AI writes it, Meta's template codes, the speech-to-text code, the
 * words that bring the menu back, greetings that put it first on the menu, the typed yes and no to "Switch to ...?",
 * and where it sits in every list. Only the fields that changed are sent; a word list replaces the whole list.
 */
export function LanguageEditDrawer({ language, onClose }: { language: admin.AdminLanguage | null; onClose: () => void }) {
  const t = useT();
  const update = useUpdateLanguage();
  const [name, setName] = useState('');
  const [nativeName, setNativeName] = useState('');
  const [quality, setQuality] = useState<string>('good');
  const [metaCodes, setMetaCodes] = useState<string[]>([]);
  const [speechCode, setSpeechCode] = useState('');
  const [words, setWords] = useState<Record<WordField, string[]>>({ change_words: [], greeting_words: [], yes_words: [], no_words: [] });
  const [sortOrder, setSortOrder] = useState<number | ''>(0);
  const [refusal, setRefusal] = useState<{ field: Field | null; message: string } | null>(null);

  useEffect(() => {
    if (!language) return;
    setName(language.name);
    setNativeName(language.native_name);
    setQuality(language.quality);
    setMetaCodes(language.meta_codes);
    setSpeechCode(language.speech_code ?? '');
    setWords({
      change_words: language.change_words,
      greeting_words: language.greeting_words,
      yes_words: language.yes_words,
      no_words: language.no_words
    });
    setSortOrder(language.sort_order);
    setRefusal(null);
  }, [language]);

  if (!language) return <Drawer opened={false} onClose={onClose} position="right" size="md" />;

  // Checked as the operator types, with the service's own limits; the service checks again on save.
  const nameError = name.trim().length === 0 || name.trim().length > NAME_MAX ? t('billing.languages.edit.nameInvalid', { max: NAME_MAX }) : undefined;
  const nativeError = nativeName.trim().length === 0 || nativeName.trim().length > NATIVE_MAX
    ? t('billing.languages.edit.nativeInvalid', { max: NATIVE_MAX })
    : undefined;
  const badMeta = metaCodes.find((c) => !META_CODE.test(c));
  const metaError = badMeta ? t('billing.languages.edit.metaInvalid', { code: badMeta }) : undefined;
  const speechError = speechCode.trim() && !SPEECH_CODE.test(speechCode.trim()) ? t('billing.languages.edit.speechInvalid') : undefined;
  const sortError = typeof sortOrder !== 'number' || !Number.isInteger(sortOrder) ? t('billing.languages.edit.sortInvalid') : undefined;
  const invalid = Boolean(nameError || nativeError || metaError || speechError || sortError);

  const change: admin.UpdateAdminLanguageRequest = {};
  if (name.trim() !== language.name) change.name = name.trim();
  if (nativeName.trim() !== language.native_name) change.native_name = nativeName.trim();
  if (quality !== language.quality && QUALITIES.includes(quality as Quality)) change.quality = quality as Quality;
  if (!same(metaCodes, language.meta_codes)) change.meta_codes = metaCodes;
  if (speechCode.trim() !== (language.speech_code ?? '')) change.speech_code = speechCode.trim();
  for (const f of WORD_FIELDS) if (!same(words[f], language[f])) change[f] = words[f];
  if (typeof sortOrder === 'number' && sortOrder !== language.sort_order) change.sort_order = sortOrder;
  const dirty = Object.keys(change).length > 0;

  /** The service's message under the field it names; anything else goes in the alert at the foot. */
  const errorFor = (field: Field, local?: string) => (refusal?.field === field ? refusal.message : local);

  function submit() {
    if (!language || invalid || !dirty) return;
    setRefusal(null);
    update.mutate({ code: language.code, change }, {
      onSuccess: () => {
        notifySuccess(t('billing.languages.edit.saved', { name: change.name ?? language.name }), t('billing.languages.title'));
        onClose();
      },
      onError: (e) => setRefusal(fieldRefusal(e) ?? { field: null, message: e instanceof Error ? e.message : t('common.unknownError') })
    });
  }

  const wordsInput = (f: WordField) => (
    <WordsInput
      key={f}
      label={t(`billing.languages.edit.${f}`)}
      description={t(`billing.languages.edit.${f}Hint`, { max: WORDS_MAX })}
      value={words[f]}
      onChange={(v) => { setWords((prev) => ({ ...prev, [f]: v })); setRefusal(null); }}
      max={WORDS_MAX}
      maxLength={WORD_LENGTH_MAX}
      lowercase
      error={errorFor(f)}
      data-testid={`language-${f}`}
    />
  );

  return (
    <Drawer opened onClose={onClose} title={t('billing.languages.edit.title', { name: language.name })} position="right" size="md">
      <Stack gap="md">
        <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('billing.languages.edit.intro', { code: language.code })}</Text>
        <TextInput
          label={t('billing.languages.edit.name')}
          value={name}
          maxLength={NAME_MAX}
          onChange={(e) => { setName(e.currentTarget.value); setRefusal(null); }}
          required
          description={t('billing.languages.edit.nameHint')}
          error={errorFor('name', nameError)}
        />
        <TextInput
          label={t('billing.languages.edit.native')}
          value={nativeName}
          maxLength={NATIVE_MAX}
          onChange={(e) => { setNativeName(e.currentTarget.value); setRefusal(null); }}
          required
          description={t('billing.languages.edit.nativeHint', { count: nativeName.trim().length, max: NATIVE_MAX })}
          error={errorFor('native_name', nativeError)}
          data-testid="language-native-name"
        />
        <Select
          label={t('billing.languages.edit.quality')}
          value={quality}
          onChange={(e) => { setQuality(e.currentTarget.value); setRefusal(null); }}
          data={QUALITIES.map((q) => ({ value: q, label: t(`billing.languages.quality.${q}`) }))}
          description={t(`billing.languages.edit.qualityHint.${quality}`, '')}
          error={errorFor('quality')}
        />
        <WordsInput
          label={t('billing.languages.edit.meta')}
          description={t('billing.languages.edit.metaHint')}
          value={metaCodes}
          onChange={(v) => { setMetaCodes(v); setRefusal(null); }}
          maxLength={6}
          error={errorFor('meta_codes', metaError)}
          data-testid="language-meta-codes"
        />
        <TextInput
          label={t('billing.languages.edit.speech')}
          value={speechCode}
          onChange={(e) => { setSpeechCode(e.currentTarget.value); setRefusal(null); }}
          description={t('billing.languages.edit.speechHint')}
          error={errorFor('speech_code', speechError)}
        />
        {WORD_FIELDS.map(wordsInput)}
        <NumberInput
          style={{ maxWidth: 200 }}
          label={t('billing.languages.edit.sortOrder')}
          value={sortOrder}
          onChange={(v) => { setSortOrder(typeof v === 'number' ? v : ''); setRefusal(null); }}
          step={10}
          description={t('billing.languages.edit.sortOrderHint')}
          error={errorFor('sort_order', sortError)}
        />
        {refusal && refusal.field === null && (
          <Alert color="error" title={t('billing.languages.edit.failed')}>{refusal.message}</Alert>
        )}
        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={onClose}>{t('action.cancel')}</Button>
          <Button loading={update.isPending} disabled={invalid || !dirty} onClick={submit}>{t('action.save')}</Button>
        </Group>
      </Stack>
    </Drawer>
  );
}
